import React, { useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import './reservedPeriodChart.scss';

import HistoricReservationService from '../historicReservationService';
import ReservationInsightsFilter from '../../interfaces/reservationInsightsFilter';
import LanguageProvider from '@/providers/languageProvider';
import ChartUtils from './chartUtils';
import IDayPercentage from '../../interfaces/IDayPercentage';
import AppEventHub, { AppEvents } from '@/utils/appEventHub';
import Colors from '@/styles/colors';
import ActuallyCenteredLoader from '@/components/loaders/actuallyCenteredLoader';

export enum TimeUnit {
    week = "week",
    month = "month",
    day = "day"
}

interface ChartProps {
    id: string;
    filter: ReservationInsightsFilter;
}

export interface SeriesData {
    label: string;
    value: number;
}

const ReservedPeriodChart: React.FC<ChartProps> = (props) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [series, setSeries] = useState<SeriesData[]>([]);
    const [selectedType, setSelectedType] = useState<TimeUnit>(TimeUnit.month);
    const [monthNames, setMonthNames] = useState<string[]>(ChartUtils.getShortHandMonthnames());

    const historicReservationService = new HistoricReservationService();

    useEffect(() => {
        setSeries([]);
        setLoading(true);

        fetchData();

        AppEventHub.on(AppEvents.LanguageChanged, updateMonthNames);

        return (): void => { AppEventHub.off(AppEvents.LanguageChanged, updateMonthNames); }

    }, [props.filter.triggerUpdate, selectedType, monthNames]);

    function fetchData(): void {
        switch (selectedType) {
            case TimeUnit.month:
                historicReservationService.getPlaceReservationsMonthPercentage(props.filter).then(response => {
                    setSeries(response.map(item => ({
                        label: `${monthNames[item.month - 1]} ${item.year}`,
                        value: item.percentage
                    })));
                    setLoading(false);
                });
                break;
            case TimeUnit.week:
                historicReservationService.getPlaceReservationsWeekPercentage(props.filter).then(response => {
                    setSeries(response.map(item => ({
                        label: `${item.week.toString()}-${item.year.toString()}`,
                        value: item.percentage
                    })));
                    setLoading(false);
                });
                break;
            case TimeUnit.day:
                historicReservationService.getPlaceReservationsDayPercentage(props.filter).then(response => {
                    const convertedResponse = ChartUtils.convertDates(response) as IDayPercentage[];
                    setSeries(convertedResponse.map(item => ({
                        label: item.day.toLocaleDateString(),
                        value: item.percentage
                    })));
                    setLoading(false);
                });
                break;
            default:
                break;
        }
    }

    function updateMonthNames(): void {
        const monthNames = ChartUtils.getShortHandMonthnames();
        setMonthNames(monthNames);
    }

    return <div className="widget-reserved-moment">
        <div className="d-flex widget-toggle-buttons">
            <button className={"btn-toggle " + (selectedType === TimeUnit.month ? "active" : "")} onClick={(): void => { setSeries([]); setSelectedType(TimeUnit.month) }}>{LanguageProvider.getTranslation("dates." + TimeUnit.month)}</button>
            <button className={"btn-toggle " + (selectedType === TimeUnit.week ? "active" : "")} onClick={(): void => { setSeries([]); setSelectedType(TimeUnit.week) }}>{LanguageProvider.getTranslation("dates." + TimeUnit.week)}</button>
            <button className={"btn-toggle " + (selectedType === TimeUnit.day ? "active" : "")} onClick={(): void => { setSeries([]); setSelectedType(TimeUnit.day) }}>{LanguageProvider.getTranslation("dates." + TimeUnit.day)}</button>
        </div>
        {!loading && series && series.length > 0 &&
            <HighchartsReact
                id={props.id}
                highcharts={Highcharts}
                options={reservedPeriodOptions(series, selectedType)} />
        }
        {(loading || !series || (series && series.length === 0)) && <ActuallyCenteredLoader/>}
    </div>;
};

export default ReservedPeriodChart;

function getMaxValuesInView(valuesCount: number, selectedType: string): number | undefined {
    const max = selectedType === TimeUnit.month ? 12 : selectedType === TimeUnit.week ? 52 : selectedType === TimeUnit.day ? 50 : 10;
    const inViewCount = valuesCount <= max ? undefined : max; // undefined means all values are shown

    return inViewCount;
}

const reservedPeriodOptions = (data: SeriesData[], selectedType: string): Highcharts.Options => (
    {
        chart: {
            type: 'column'
        },
        title: ChartUtils.getTitle(`${LanguageProvider.getTranslation("pages.realestateutilization.charts.reservedtimebarchart.title")} (${LanguageProvider.getTranslation("dates." + selectedType)})`),
        xAxis: {
            type: 'category',
            min: 0,
            max: getMaxValuesInView(data.length, selectedType),
            categories: data.map(d => `${selectedType === TimeUnit.week ? 'wk' : ''} ${d.label}`),
            scrollbar: {
                enabled: !!getMaxValuesInView(data.length, selectedType),
            },
            title: {
                text: '',
            },
            labels: {
                rotation: -75
            }
        },
        yAxis: {
            min: 0,
            max: 100,
            title: {
                text: '',
            },
            tickInterval: 25,
            labels: {
                formatter: function (): string {
                    return `${this.value}%`;
                },
            }
        },
        series: [
            {
                type: 'column',
                data: data.map(d => Math.round(d.value)),
                showInLegend: false,
                color: Colors.shakespeare_blue,
                dataLabels: {
                    enabled: true,
                    format: '{point.y}%'
                }
            },
        ],
        tooltip: {
            formatter: function () {
                return `<b>${this.key}</b>: ${this.y}%`;
            }
        },
        accessibility: {
            enabled: false
        },
        credits: {
            enabled: false
        },
        lang: ChartUtils.getLanguageOptions(),
        exporting: ChartUtils.getExportingOptions(),
    });